/**
 * What ran out, for the workspace whose product is shut.
 *
 * Three screens word that fact — the dashboard's strip, the card at the foot of
 * the rail, and every create button that has gone grey — and until now each
 * worked it out for itself from the same two records, with the same comment
 * copied beside it three times. The rule is here instead, because a practice
 * that reads "your free trial has ended" on the dashboard and "you need a plan"
 * in a toast is being told about two different accounts.
 *
 * ## Why the subscription record cannot answer it alone
 *
 * `GET /billing/subscription/` carries the row that is **running** and nothing
 * else. A practice that has bought prepaid terms for a year has `subscription:
 * null` the morning its last one ends — so "no subscription and a trial on the
 * record" is true both of somebody whose fortnight just ran out and of a
 * customer of twelve months' standing. The trial never leaves the record, and
 * reading the absence of a plan as evidence of a trial is what told the second
 * one their free trial had ended.
 *
 * What says they have bought something is the term: `GET
 * /billing/prepaid/terms/` keeps the finished one as `current` with status
 * `EXPIRED`, so a term on the record — whatever its status — is a purchase that
 * happened. The legacy mandate row counts the same way for the workspaces still
 * on one.
 */

import type { PrepaidTerms } from "./types/prepaid.types";
import type { Subscription, Trial } from "./types/subscriptions.types";

/**
 * What ended, where it can be told.
 *
 * `null` is three different situations and deliberately one value: the product
 * is open, or nothing has ever been bought or trialled, or the terms could not
 * be read and this app will not guess between the two. Every screen that words
 * it treats `null` as "say nothing about what ended" and makes the ordinary
 * offer instead, which is true of all three.
 */
export type AccessLapse = "trial" | "plan" | null;

export interface AccessLapseFacts {
  /**
   * Whether the product is shut — `access_source` is `NONE` and the read has
   * answered. Nothing below is asked while it is open: a practice on its trial
   * with days left has had nothing run out.
   */
  locked: boolean;
  /** The mandate row, for the workspaces still on one. `null` for every
   *  prepaid practice, running or lapsed — see above. */
  subscription: Subscription | null;
  /** The fortnight, running or finished, or `null` for a workspace that never
   *  took one. */
  trial: Trial | null;
  /**
   * The prepaid terms, or `null` where they could not be read — a failure, a
   * read still out, or the `403` a staff member gets from an owner-only call.
   *
   * `null` is not "no terms". It is "we do not know", and it is what stops the
   * trial's wording being put to a practice whose purchase this app simply
   * could not see.
   */
  terms: PrepaidTerms | null;
}

export const accessLapse = ({
  locked,
  subscription,
  trial,
  terms,
}: AccessLapseFacts): AccessLapse => {
  if (!locked) return null;

  // A purchase on the record, of either kind. `queued` as well as `current`
  // because a term bought to follow one that has ended is still a practice
  // that pays — the door is shut because the new one has not started yet.
  if (subscription || terms?.current || terms?.queued) return "plan";

  // Only once the terms have actually answered. Without them, "no plan on the
  // record" is not evidence of anything.
  if (!terms) return null;

  return trial ? "trial" : null;
};
